import React, { useState, useRef, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Button, Card, CardBody } from 'reactstrap';
import Icon from '../containers/icon';
import AiChatMessage from './aiChatMessage';

/**
 * AI Chat Input Component
 *
 * A sophisticated chat interface similar to cursor window with:
 * - Message history display
 * - Real-time typing indicators
 * - Conversation threading
 * - Auto-scroll to latest messages
 * - Message timestamps
 */
const defaultOnClear = () => {};
const defaultOnSendMessage = () => {};
const AiChatInput = ({
  className: _className = '',
  onClear: _onClear = defaultOnClear,
  placeholder,
  text: _text = '',
  messages: _messages = [],
  onSendMessage: _onSendMessage = defaultOnSendMessage,
  isLoading: _isLoading = false,
  waitingMessage,
  emptyStateMessage
}) => {
  const [currentMessage, setCurrentMessage] = useState(_text || '');
  const [localMessages, setLocalMessages] = useState(_messages);
  const [conversationId] = useState(Date.now().toString());
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const lastTextRef = useRef(_text);

  // Auto-scroll to bottom when new messages arrive
  const scrollToBottom = useCallback(() => {
    var _messagesEndRef$curre;
    (_messagesEndRef$curre = messagesEndRef.current) === null || _messagesEndRef$curre === void 0 || _messagesEndRef$curre.scrollIntoView({
      behavior: 'smooth'
    });
  }, []);
  useEffect(() => {
    scrollToBottom();
  }, [localMessages, scrollToBottom]);

  // Sync local messages with prop, but preserve existing conversation
  useEffect(() => {
    if (_messages.length > 0) {
      // Only add new error messages, don't replace the entire conversation
      const newErrorMessages = _messages.filter(msg => msg.type === 'system' && !localMessages.some(existing => existing.id === msg.id));
      if (newErrorMessages.length > 0) {
        // Remove any waiting messages when error messages are received
        setLocalMessages(prev => {
          const messagesWithoutWaiting = prev.filter(msg => !msg.isWaiting);
          return [...messagesWithoutWaiting, ...newErrorMessages];
        });
      }
    }
  }, [_messages, localMessages]);

  // Keep internal state updated when parent clears/changes value
  useEffect(() => {
    if (_text !== lastTextRef.current) {
      setCurrentMessage(_text || '');
      lastTextRef.current = _text;
    }
  }, [_text]);

  // Remove waiting messages when loading state changes from true to false
  useEffect(() => {
    if (!_isLoading) {
      setLocalMessages(prev => prev.filter(msg => !msg.isWaiting));
    }
  }, [_isLoading]);
  const handleSendMessage = () => {
    if (currentMessage.trim()) {
      const userMessage = {
        id: `${conversationId}-${Date.now()}-user`,
        content: currentMessage.trim(),
        type: 'user',
        timestamp: new Date().toISOString(),
        conversationId
      };

      // Add user message to local messages
      const updatedMessages = [...localMessages, userMessage];

      // Add waiting response
      const waitingMessageObj = {
        id: `${conversationId}-${Date.now()}-waiting`,
        content: waitingMessage,
        type: 'assistant',
        timestamp: new Date().toISOString(),
        isWaiting: true,
        conversationId
      };
      const messagesWithWaiting = [...updatedMessages, waitingMessageObj];

      // Update local messages state
      setLocalMessages(messagesWithWaiting);
      if (_onSendMessage) {
        // Call the parent's onSendMessage with message context
        _onSendMessage(currentMessage.trim(), {
          conversationId,
          messageId: userMessage.id,
          waitingMessageId: waitingMessageObj.id,
          conversationHistory: updatedMessages
        });
      }
      setCurrentMessage('');
    }
  };
  const handleClear = () => {
    if (_onClear) {
      _onClear();
      setCurrentMessage('');
    }
  };
  const handleKeyDown = event => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSendMessage();
    }
  };
  return /*#__PURE__*/React.createElement("div", {
    className: `ai-chat-input ${_className}`
  }, /*#__PURE__*/React.createElement(Card, {
    className: "chat-container",
    style: {
      height: '400px',
      display: 'flex',
      flexDirection: 'column'
    }
  }, /*#__PURE__*/React.createElement(CardBody, {
    className: "chat-messages p-3",
    style: {
      flex: 1,
      overflowY: 'auto',
      minHeight: 0
    }
  }, localMessages.length === 0 ? /*#__PURE__*/React.createElement("div", {
    className: "text-center text-muted mt-4"
  }, /*#__PURE__*/React.createElement(Icon, {
    glyph: "comments",
    size: "2x"
  }), /*#__PURE__*/React.createElement("p", {
    className: "mt-2"
  }, emptyStateMessage)) : localMessages.map((message, index) => /*#__PURE__*/React.createElement(AiChatMessage, {
    key: message.id || index,
    message: message
  })), /*#__PURE__*/React.createElement("div", {
    ref: messagesEndRef
  })), /*#__PURE__*/React.createElement("div", {
    className: "chat-input-container p-3 border-top",
    style: {
      flexShrink: 0
    }
  }, /*#__PURE__*/React.createElement("div", {
    className: "input-group"
  }, /*#__PURE__*/React.createElement("textarea", {
    ref: inputRef,
    "data-testid": "t-ai-chat-input",
    value: currentMessage,
    placeholder: placeholder,
    onChange: e => setCurrentMessage(e.target.value),
    className: "form-control t-ai-chat-input tqc-control",
    onKeyDown: handleKeyDown,
    rows: 2,
    disabled: _isLoading || localMessages.some(message => message.isWaiting),
    style: {
      resize: 'none'
    }
  }), /*#__PURE__*/React.createElement("div", {
    className: "input-group-append"
  }, currentMessage.trim() && /*#__PURE__*/React.createElement(Button, {
    color: "outline-secondary",
    size: "sm",
    className: "me-2",
    onClick: handleClear,
    disabled: _isLoading || localMessages.some(message => message.isWaiting),
    "data-testid": "t-ai-chat-clear"
  }, /*#__PURE__*/React.createElement(Icon, {
    glyph: "times-circle"
  })), /*#__PURE__*/React.createElement(Button, {
    color: "primary",
    size: "sm",
    onClick: handleSendMessage,
    disabled: !currentMessage.trim() || _isLoading || localMessages.some(message => message.isWaiting),
    "data-testid": "t-ai-chat-send"
  }, /*#__PURE__*/React.createElement(Icon, {
    glyph: "arrow-up"
  })))))));
};
AiChatInput.propTypes = {
  className: PropTypes.string,
  onClear: PropTypes.func,
  placeholder: PropTypes.string.isRequired,
  text: PropTypes.string,
  messages: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    content: PropTypes.string.isRequired,
    type: PropTypes.oneOf(['user', 'assistant', 'system']).isRequired,
    timestamp: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    conversationId: PropTypes.string,
    isWaiting: PropTypes.bool
  })),
  onSendMessage: PropTypes.func,
  isLoading: PropTypes.bool,
  waitingMessage: PropTypes.string.isRequired,
  emptyStateMessage: PropTypes.string.isRequired
};
export default AiChatInput;